import React, { useState } from 'react';
import { Calculator, TrendUp } from '@phosphor-icons/react';
import { SectionHeading } from './SiteBlocks';

const INR_RATE = 86;
const RATES = {
  agency: { label: 'Traditional Agency', perDevMonth: 16000, note: 'US/UK agency blended rate (~$100/hr)' },
  freelancer: { label: 'Freelancers', perDevMonth: 7200, note: 'Senior freelancers (~$45/hr) + your own PM overhead' },
  xceed: { label: 'XceedAgents', perDevMonth: 8500, speedFactor: 0.6, note: 'Senior engineers + 8 AI agents — 60% faster delivery' },
};

function fmt(amount, currency) {
  if (currency === 'INR') {
    const inr = amount * INR_RATE;
    if (inr >= 10000000) return `₹${(inr / 10000000).toFixed(2)} Cr`;
    return `₹${(inr / 100000).toFixed(1)} L`;
  }
  return `$${Math.round(amount).toLocaleString('en-US')}`;
}

export default function SavingsCalculator() {
  const [team, setTeam] = useState(3);
  const [months, setMonths] = useState(4);
  const [currency, setCurrency] = useState('USD');

  const agencyCost = RATES.agency.perDevMonth * team * months;
  const freelancerCost = RATES.freelancer.perDevMonth * team * months;
  const xceedCost = RATES.xceed.perDevMonth * team * months * RATES.xceed.speedFactor;
  const maxCost = agencyCost;

  const savingsVsAgency = agencyCost - xceedCost;
  const savingsPct = Math.round((savingsVsAgency / agencyCost) * 100);

  const rows = [
    { key: 'agency', cost: agencyCost, color: '#64748B', ...RATES.agency },
    { key: 'freelancer', cost: freelancerCost, color: '#94A3B8', ...RATES.freelancer },
    { key: 'xceed', cost: xceedCost, color: '#EA580C', ...RATES.xceed },
  ];

  return (
    <section className="py-20 lg:py-28" style={{ background: 'var(--background)' }} data-testid="savings-calculator-section">
      <div className="max-w-6xl mx-auto px-6">
        <SectionHeading
          eyebrow="Savings Calculator"
          title="See how much you save building with XceedAgents."
          subtitle="Compare the cost of a traditional agency and freelancers against our hybrid AI + senior-engineer model. Adjust team size and duration to match your project."
        />

        <div className="mt-12 grid lg:grid-cols-5 gap-6">
          {/* Controls */}
          <div className="lg:col-span-2 p-7 rounded-2xl border bg-white" style={{ borderColor: 'var(--border)' }}>
            <div className="flex items-center gap-2 mb-6">
              <Calculator size={20} weight="duotone" style={{ color: 'var(--primary-accent)' }} />
              <span className="font-heading font-bold" style={{ color: 'var(--text-primary)' }}>Your project</span>
            </div>

            <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>
              Team size: <span style={{ color: 'var(--primary-accent)' }}>{team} engineer{team > 1 ? 's' : ''}</span>
            </label>
            <input
              type="range" min="1" max="10" value={team}
              onChange={(e) => setTeam(Number(e.target.value))}
              className="w-full accent-[#EA580C]"
              data-testid="calc-team-slider"
            />

            <label className="block text-sm font-semibold mb-2 mt-6" style={{ color: 'var(--text-secondary)' }}>
              Duration: <span style={{ color: 'var(--primary-accent)' }}>{months} month{months > 1 ? 's' : ''}</span>
            </label>
            <input
              type="range" min="1" max="12" value={months}
              onChange={(e) => setMonths(Number(e.target.value))}
              className="w-full accent-[#EA580C]"
              data-testid="calc-duration-slider"
            />

            <div className="mt-6">
              <div className="text-sm font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Currency</div>
              <div className="inline-flex rounded-lg border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
                {['USD', 'INR'].map((c) => (
                  <button
                    key={c}
                    onClick={() => setCurrency(c)}
                    className="px-4 py-1.5 text-sm font-semibold transition-colors"
                    style={currency === c
                      ? { background: 'var(--primary-accent)', color: '#fff' }
                      : { background: 'transparent', color: 'var(--text-secondary)' }}
                    data-testid={`calc-currency-${c.toLowerCase()}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>

            <p className="mt-6 text-xs leading-relaxed" style={{ color: 'var(--text-muted)' }}>
              Estimates based on typical 2026 market rates. XceedAgents cost reflects our AI-accelerated delivery
              (same scope, ~40% fewer dev-months).
            </p>
          </div>

          {/* Results */}
          <div className="lg:col-span-3 p-7 rounded-2xl border bg-white" style={{ borderColor: 'var(--border)' }}>
            <div className="space-y-6">
              {rows.map((r) => (
                <div key={r.key} data-testid={`calc-cost-${r.key}`}>
                  <div className="flex items-baseline justify-between mb-1.5">
                    <div>
                      <span className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{r.label}</span>
                      <span className="ml-2 text-xs hidden sm:inline" style={{ color: 'var(--text-muted)' }}>{r.note}</span>
                    </div>
                    <span className="font-heading text-xl font-bold" style={{ color: r.key === 'xceed' ? 'var(--primary-accent)' : 'var(--text-primary)' }}>
                      {fmt(r.cost, currency)}
                    </span>
                  </div>
                  <div className="h-3 rounded-full overflow-hidden" style={{ background: 'var(--surface)' }}>
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${Math.max((r.cost / maxCost) * 100, 6)}%`, background: r.color }}
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 p-5 rounded-xl flex flex-wrap items-center justify-between gap-3" style={{ background: 'rgba(234,88,12,0.08)', border: '1px solid rgba(234,88,12,0.25)' }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(234,88,12,0.15)' }}>
                  <TrendUp size={20} weight="bold" style={{ color: 'var(--primary-accent)' }} />
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-secondary)' }}>You save vs a traditional agency</div>
                  <div className="font-heading text-2xl font-bold" style={{ color: 'var(--primary-accent)' }} data-testid="calc-savings-amount">
                    {fmt(savingsVsAgency, currency)} <span className="text-base font-semibold">({savingsPct}% less)</span>
                  </div>
                </div>
              </div>
              <a href="/start-project" className="h-10 px-5 rounded-md font-semibold text-sm flex items-center" style={{ background: 'var(--primary-accent)', color: '#fff' }} data-testid="calc-cta">
                Request for Proposal
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
